import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { BookOpen, ExternalLink, Plus, Pencil, Lock, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import BootcampHubCard from "@/components/bootcamp/BootcampHubCard";
import VettingHubCard from "@/components/vetting/VettingHubCard";
import TalkLibraryHubCard from "@/components/TalkLibraryHubCard";

const MIN_ROLES = [
  { value: "partner", label: "All signed-in users" },
  { value: "leader", label: "Leader+ (Leaders, Platinums, Oversight, Admin)" },
  { value: "platinum", label: "Platinum+" },
  { value: "oversight", label: "Oversight + Admin" },
  { value: "admin", label: "Admin only" },
];

// Resource cards that now open a native Hub guide (the Google Sheet stays linked inside the guide).
const NATIVE_GUIDES = [
  { sheet: "1xQux9Ey_LM9--kDUH8nDW46_0L7rxcATRi-uvk1dIeA", to: "/appointments",
    description: "Your step-by-step guide to preparing for appointments, communicating with your PBO and following through." },
  { sheet: "1YsJi_k523v4aJnG3tJMYH-ItrDLAn5RwbCs_T5Yx9UU", to: "/network" },
  { sheet: "1FNBaopQ1btVEYSplpyQYNrRn9q_k1v127pptIehzuH0", to: "/leadership" },
  { sheet: "1Ra9wiEiOqTXUpCUBnivFZaNM20fikDgbyGALJLCC8TU", to: "/new-partner-guide" },
];
const nativeGuide = (r) => NATIVE_GUIDES.find((g) => (r.url || "").includes(g.sheet));

export default function ATLHub() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", url: "", min_role: "partner", display_order: 0, active: true });

  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();

  const [loadError, setLoadError] = useState(null);
  const load = async () => {
    try {
      const { data } = await api.get("/resources", { params: isAdmin ? { include_inactive: true } : {} });
      setList(data);
      setLoadError(null);
    } catch (e) {
      setLoadError(e?.response?.data?.detail || "Couldn't load resources. Check your connection and try again.");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const open = (resource = null) => {
    setEditing(resource || "new");
    if (resource) setForm({
      name: resource.name || "",
      description: resource.description || "",
      url: resource.url || "",
      min_role: resource.min_role || "partner",
      display_order: resource.display_order || 0,
      active: resource.active !== false,
    });
    else setForm({ name: "", description: "", url: "", min_role: "partner", display_order: (list.length + 1) * 10, active: true });
  };

  const save = async () => {
    try {
      if (editing === "new") {
        await api.post("/resources", form);
        toast.success("Resource added");
      } else {
        await api.patch(`/resources/${editing.id}`, form);
        toast.success("Resource saved");
      }
      setEditing(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openResource = async (r) => {
    try {
      // Server re-checks role so a Partner cannot deep-link to Leadership Hub.
      const { data } = await api.get(`/resources/${r.id}`);
      if (!data.url) {
        toast.error("This resource hasn't been configured yet. Ask Sean or Rina to paste the Google Sheet link.");
        return;
      }
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error(e?.response?.data?.detail || "Access denied"); }
  };

  return (
    <div className="space-y-6" data-testid="hub-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-[#C9A961] font-semibold">Resources</div>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#0A1F44] mt-1">Resources</h1>
          <p className="text-sm text-slate-500 mt-1">Guides, training and leadership resources — everything you need is right here in the Hub.</p>
        </div>
        {isAdmin && (
          <Button onClick={() => open()} data-testid="hub-add-btn" className="rounded-full bg-[#0A1F44] text-white">
            <Plus className="w-4 h-4 mr-1" /> Add resource
          </Button>
        )}
      </div>

      <BootcampHubCard />
      <VettingHubCard />
      <TalkLibraryHubCard />

      <div className="grid sm:grid-cols-2 gap-4">
        {list.map((r) => {
          const needsUrl = !r.url;
          const native = nativeGuide(r);
          return (
            <Card key={r.id} className="p-5 relative" data-testid={`hub-card-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-[#0A1F44] text-[#C9A961] flex items-center justify-center shrink-0">
                    <BookOpen className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-serif text-lg text-[#0A1F44]">{r.name}</div>
                    {r.min_role && r.min_role !== "partner" && (
                      <div className="text-[10px] uppercase tracking-wider text-[#8A6D1F] mt-0.5 flex items-center gap-1">
                        <Lock className="w-3 h-3" /> {r.min_role} access
                      </div>
                    )}
                    <div className="text-xs text-slate-600 mt-2">{native?.description || r.description}</div>
                  </div>
                </div>
                {isAdmin && (
                  <button onClick={() => open(r)} data-testid={`hub-edit-${r.id}`}
                          className="text-slate-400 hover:text-[#0A1F44]">
                    <Pencil className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="mt-4 flex items-center justify-between">
                {native ? (
                  <span className="text-xs text-slate-500">Team Smith Hub guide</span>
                ) : needsUrl ? (
                  <span className="text-xs text-[#8A1F1F]">Awaiting link — Admin to configure</span>
                ) : (
                  <span className="text-xs text-slate-500">Google Sheet</span>
                )}
                <Button
                  size="sm"
                  onClick={() => (native ? navigate(native.to) : openResource(r))}
                  disabled={!native && needsUrl && !isAdmin}
                  data-testid={`hub-open-${r.id}`}
                  className="rounded-full min-h-[44px] px-4 bg-[#0A1F44] text-white hover:bg-[#0A1F44]/90"
                >
                  {native ? <ArrowRight className="w-4 h-4 mr-1" /> : <ExternalLink className="w-4 h-4 mr-1" />} Open
                </Button>
              </div>
            </Card>
          );
        })}
        {loadError && (
          <div className="text-sm text-slate-800 rounded-lg border border-amber-200 bg-amber-50 p-3 flex flex-wrap items-center gap-3" data-testid="resources-load-error">
            {loadError}<Button size="sm" variant="outline" className="min-h-[44px]" onClick={load} data-testid="resources-retry">Try again</Button>
          </div>
        )}
        {!loadError && list.length === 0 && <div className="text-sm text-slate-600">No resources available yet.</div>}
      </div>

      {/* Admin: add/edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-[#0A1F44]">
              {editing === "new" ? "Add resource" : `Edit — ${editing?.name}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="hub-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Description</Label><Input data-testid="hub-form-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label>Google Sheet URL</Label><Input data-testid="hub-form-url" type="url" placeholder="https://docs.google.com/..." value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} /></div>
            <div>
              <Label>Access level</Label>
              <Select value={form.min_role} onValueChange={(v) => setForm({ ...form, min_role: v })}>
                <SelectTrigger data-testid="hub-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MIN_ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Display order</Label><Input type="number" data-testid="hub-form-order" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value || "0", 10) })} /></div>
              <div>
                <Label>Status</Label>
                <Select value={form.active ? "true" : "false"} onValueChange={(v) => setForm({ ...form, active: v === "true" })}>
                  <SelectTrigger data-testid="hub-form-active"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} data-testid="hub-form-save" className="bg-[#0A1F44] text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
